import { type FC, useCallback } from 'react';
import type { SampleDoseTimeColumn } from '@/components/UI/DataTableV2/DataTable.model';
import { type DataTableColumnUpdateV1SampleDoseTime } from '@/components/UI/DataTableV2/DataTable.model';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { Controller, useForm } from 'react-hook-form';
import Switch from '@/components/UI/FormElements/Switch';
import { DateUtils } from '@/utils/Date.utils';
import { TimepointInput } from '@/components/UI/Timepoint/TimepointInput';
import { _isNotBlank } from '@/littledash';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { errorToast } from '@/helpers';

type ColumnFormData = { clash_window: string | null };
type EditSampleDoseTimeModalProps = {
  column: SampleDoseTimeColumn;
  onCancel: () => Promise<void>;
  onSave: (data: DataTableColumnUpdateV1SampleDoseTime) => Promise<void>;
};

export const EditSampleDoseTimeModal: FC<EditSampleDoseTimeModalProps> = ({ column, onSave, onCancel }) => {
  const handleSubmit = useCallback(
    async (data: ColumnFormData) => {
      try {
        await onSave({ type: 'sampleDoseTime', clash_window: data.clash_window });
        await onCancel();
      } catch (error) {
        errorToast('Failed to update column');
        ExceptionHandler.captureException(
          new InVivoError('Could not update sample dose time column', { cause: error, slug: 'update-sample-dose-time' })
        );
      }
    },
    [onSave, onCancel]
  );
  const formMethods = useForm<{ clash_window: string | null }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      clash_window: column?.clash_window ?? null,
    },
  });

  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Edit column" closeModal={onCancel} />
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        data-test-component="EditSampleDoseTimeModal"
        data-test-element="form"
      >
        <Controller
          control={formMethods.control}
          name="clash_window"
          rules={{ validate: DateUtils.validateClashWindow }}
          render={({ field, fieldState }) => {
            const isEnabled = _isNotBlank(field.value);
            return (
              <div className="pa3 flex flex-column">
                <div className="flex flex-row justify-between items-center" data-test-element="switch-container">
                  <label>Clash window</label>
                  <Switch value={isEnabled} onChange={(value) => field.onChange(value ? 'PT3M' : null)} />
                </div>
                {isEnabled ? (
                  <>
                    <label className="mt2 db">Warn me when timestamps are within:</label>
                    <div className="mt2" data-test-element="duration-input-container">
                      <TimepointInput
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(_isNotBlank(value.value) ? value.value : null);
                        }}
                        defaultUnit="minutes"
                        onBlur={field.onBlur}
                        invalid={fieldState.invalid}
                        disabled={field.disabled}
                      />
                    </div>
                    {fieldState.invalid ? <p className="f6 red db pv1">{fieldState.error?.message}</p> : null}
                  </>
                ) : null}
              </div>
            );
          }}
        />
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        submitBtnText="Save"
        submitButtonProps={{
          disabled:
            !(formMethods.formState.isValid && formMethods.formState.isDirty) || formMethods.formState.isSubmitting,
        }}
        onCancel={onCancel}
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
